<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import { mapActions, mapGetters, mapState } from "vuex";
import Swal from "sweetalert2";

export default {
  page: {
    title: "Add Items",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },

  components: {
    Layout,
    PageHeader,
  },

  data() {
    return {
      title: "Add Items",

      items: [
        {
          text: "Home",
          href: "/",
        },
        {
          text: "Add Items",
          active: true,
        },
      ],

      modal_id: "",
      existingProduct: [],

      nameError: "",
      unitError: "",
      categoryError: "",
      imageError: "",

      data: {
        name: "",
        category: "",
        quantity: 0,
        unit: "",
        selling_price: "",
        image: null,
      },
    };
  },

  computed: {
    ...mapState("notification", ["type", "message", "show"]),

    ...mapGetters("marketplace", [
      "saveStatus",
      "Units",
      "MarketCategories",
      "MarketProducts",
    ]),
  },

  watch: {
    show(newValue) {
      if (newValue === true) {
        if (this.type === "success") {
          Swal.fire("Success!", this.message.msg, "success");
          this.closeModal(this.modal_id);
        } else {
          Swal.fire("Error!", this.message.msg, "error");
        }
      }
    },
  },

  methods: {
    ...mapActions({
      FetchProducts: "marketplace/fetchMarketProducts",
      FetchUnits: "marketplace/fetchUnits",
      FetchCategories: "marketplace/fetchMarketCategories",
      AddProduct: "marketplace/addMarketProduct",
    }),

    assignName() {
      this.data.name = this.data.product;
    },

    checkName() {
      if (this.data.name !== "") {
        this.existingProduct = this.MarketProducts.filter((product) => {
          return (
            product.name.toUpperCase() ===
            this.data.name.toUpperCase()
          );
        });

        if (this.existingProduct.length) {
          this.nameError = this.data.name + " already exists";
        } else {
          this.nameError = "";
        }
      }
    },

    thousandSeparator(value) {
      if (!value) return "";

      return value
        .toString()
        .replace(/[^\d.]/g, "")
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },

    addSellCommas() {
      this.data.selling_price = this.thousandSeparator(
        this.data.selling_price
      );
    },

    handleImageUpload(event) {
      const file = event.target.files[0];

      if (!file) return;

      // Validate image size (> 2MB)
      if (file.size > 2 * 1024 * 1024) {
        this.imageError = "Image must be less than 2MB";
        this.data.image = null;
        return;
      }

      this.imageError = "";
      this.data.image = file;
    },

    addProduct(modalId) {
      this.modal_id = modalId;

      if (this.data.category === "") {
        this.categoryError = "Category is required";
        return;
      }

      if (this.data.name === "") {
        this.nameError = "Item name is required";
        return;
      }

      if (this.existingProduct.length) {
        this.nameError = "Item already exists";
        return;
      }

      if (this.data.unit === "") {
        this.unitError = "Unit is required";
        return;
      }

      if (!this.data.image) {
        this.imageError = "Product image is required";
        return;
      }

      const formData = new FormData();

      formData.append("name", this.data.name);
      formData.append("category", this.data.category);
      formData.append("quantity", this.data.quantity);
      formData.append("unit", this.data.unit);
      formData.append("selling_price", this.data.selling_price);
      formData.append("image", this.data.image);

      this.AddProduct(formData);
    },

    resetForm() {
      this.data = {
        name: "",
        category: "",
        quantity: 0,
        unit: "",
        selling_price: "",
        image: null,
      };

      this.nameError = "";
      this.unitError = "";
      this.categoryError = "";
      this.imageError = "";
    },

    closeModal(modalId) {
      this.resetForm();

      this.$nextTick(() => {
        this.$bvModal.hide(modalId);
      });
    },
  },

  created() {
    this.FetchProducts();
    this.FetchUnits();
    this.FetchCategories();
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="card">
      <div class="card-body">
        <div class="row px-5">

          <!-- CATEGORY -->
          <div class="col-md-6">
            <div class="form-group mb-3">
              <label>
                Select Item Category
                <span class="text-danger">*</span>
              </label>

              <input
                class="form-control"
                v-model="data.category"
                list="categories"
                placeholder="Select Category"
              />

              <datalist id="categories">
                <option
                  v-for="(cat, c) in MarketCategories"
                  :key="c"
                >
                  {{ cat.name }}
                </option>
              </datalist>

              <div class="text-danger" v-if="categoryError">
                <em>{{ categoryError }}</em>
              </div>
            </div>
          </div>

          <!-- ITEM NAME -->
          <div class="col-md-6">
            <div class="form-group mb-3">
              <label>Product Name</label>

              <input
                type="text"
                class="form-control"
                v-model="data.name"
                placeholder="Enter Product Name"
                @blur="checkName()"
              />
            </div>
            <div class="text-danger" v-if="nameError">
              <em>{{ nameError }}</em>
            </div>
          </div>

          <!-- QUANTITY -->
          <div class="col-md-6">
            <div class="form-group mb-3">
              <label>Quantity In Stock</label>

              <input
                type="number"
                class="form-control"
                v-model="data.quantity"
              />
            </div>
          </div>

          <!-- UNIT -->
          <div class="col-md-6">
            <div class="form-group mb-3">
              <label>
                Item Unit
                <span class="text-danger">*</span>
              </label>

              <input
                class="form-control"
                v-model="data.unit"
                list="units"
                placeholder="Enter Item Unit"
              />

              <datalist id="units">
                <option
                  v-for="(unit, u) in Units"
                  :key="u"
                >
                  {{ unit.symbol }}
                </option>
              </datalist>

              <div class="text-danger" v-if="unitError">
                <em>{{ unitError }}</em>
              </div>
            </div>
          </div>

          <!-- PRICE -->
          <div class="col-md-6">
            <div class="form-group mb-3">
              <label>
                Selling Price
                <span class="text-danger">*</span>
              </label>

              <input
                type="text"
                class="form-control"
                v-model="data.selling_price"
                placeholder="Enter Selling Price"
                @input="addSellCommas()"
              />
            </div>
          </div>

          <!-- IMAGE -->
          <div class="col-md-6">
            <div class="form-group mb-3">
              <label>
                Product Image
                <span class="text-danger">*</span>
              </label>

              <input
                type="file"
                class="form-control"
                accept="image/*"
                @change="handleImageUpload"
              />

              <small class="text-muted">
                Image must be less than 2MB
              </small>

              <div class="text-danger" v-if="imageError">
                <em>{{ imageError }}</em>
              </div>
            </div>
          </div>

          <!-- BUTTON -->
          <div class="col-12 text-end">
            <button
              type="button"
              class="btn btn-primary"
              @click="addProduct('add-product-modal')"
            >
              <i class="fa fa-check"></i>
              Submit

              <b-spinner
                v-if="saveStatus"
                variant="light"
                small
              ></b-spinner>
            </button>
          </div>

        </div>
      </div>
    </div>
  </Layout>
</template>

<style>
.btn-primary,
.page-link {
  background-color: #FAB031 !important;
  border-color: #FAB031 !important;
}
</style>